import React, { useState } from 'react';
import { BookingPatientForm } from './BookingFormParts';
import BookingCategoryStep from './BookingCategoryStep';
import BookingStaffVerify from './BookingStaffVerify';
import BookingDependentPick from './BookingDependentPick';
import BookingOtpPanel from './BookingOtpPanel';
import {
  familyDependents,
  isPortalFacultyCategories,
  isSelfOnlyCategories,
  selfFormFromPrefill,
} from './bookingDetailsHelpers';
import { submitPortalBook, submitPublicBook, validateBookingForm } from './bookingSubmit';

const emptyForm = {
  name: '', email: '', phone: '', age: '', gender: '', symptoms: '',
  website: '', relationship: '', employee_id: '', campus_member_id: '',
};

/** Consulting identity + OTP (or session book when skipEmailOtp). */
export default function BookingDetailsFlow({
  slug, primary, picked, visitType = 'consulting', onBack, onSuccess,
  allowedCategories, prefillPatient, familyMembers, skipEmailOtp = false,
}) {
  const selfOnly = isSelfOnlyCategories(allowedCategories);
  const portalFaculty = isPortalFacultyCategories(allowedCategories);
  const familyDeps = familyDependents(familyMembers);

  const [phase, setPhase] = useState(selfOnly ? 'form' : 'category');
  const [category, setCategory] = useState(selfOnly ? 'self' : '');
  const [form, setForm] = useState(() => ({
    ...emptyForm,
    ...(selfOnly && prefillPatient ? selfFormFromPrefill(prefillPatient) : {}),
  }));
  const [locked, setLocked] = useState(Boolean(selfOnly && prefillPatient?.name));
  const [deps, setDeps] = useState([]);
  const [notice, setNotice] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const goForm = (overrides = {}, lock = false) => {
    setForm((f) => ({ ...f, ...overrides }));
    setLocked(lock);
    setPhase('form');
  };

  const runSubmit = async (otpPayload) => {
    setSubmitting(true);
    setError('');
    try {
      const data = skipEmailOtp
        ? await submitPortalBook(form, picked, visitType, category)
        : await submitPublicBook(slug, form, picked, visitType, category, otpPayload);
      onSuccess(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not submit — please try again.');
      if (!skipEmailOtp) setPhase('otp');
    }
    setSubmitting(false);
  };

  const onPickCategory = (id) => {
    setCategory(id);
    setNotice('');
    if (id === 'self') {
      const pref = selfFormFromPrefill(prefillPatient);
      goForm(pref, Boolean(pref.name));
      return;
    }
    if (id === 'non_dependent') { goForm({ relationship: '' }, false); return; }
    if (portalFaculty) { setDeps(familyDeps); setPhase('deps'); return; }
    setPhase('verify');
  };

  if (phase === 'category') {
    return (
      <BookingCategoryStep primary={primary} value={category}
        allowedCategories={allowedCategories} onBack={onBack} onPick={onPickCategory} />
    );
  }

  if (phase === 'verify') {
    return (
      <BookingStaffVerify slug={slug} primary={primary}
        mode={category === 'dependent' ? 'dependent' : 'staff'}
        onBack={() => setPhase('category')}
        onNotFound={(msg, empId) => {
          setNotice(msg); setCategory('non_dependent');
          goForm({ employee_id: empId, relationship: '' }, false);
        }}
        onVerified={(payload) => {
          if (category === 'dependent') {
            setDeps(payload.dependents || []);
            setForm((f) => ({ ...f, employee_id: payload.employeeId }));
            setPhase('deps');
            return;
          }
          const m = payload.member;
          const c = (payload.contact || '').trim();
          goForm({
            name: m.name,
            email: c.includes('@') ? c : '',
            phone: c.includes('@') ? '' : c,
            employee_id: payload.employeeId,
            campus_member_id: m.id,
            relationship: 'self',
          }, true);
        }}
      />
    );
  }

  if (phase === 'deps') {
    return (
      <BookingDependentPick dependents={deps} primary={primary}
        onBack={() => setPhase('category')}
        onManual={() => goForm({ relationship: 'spouse' }, false)}
        onPick={(d) => goForm({
          name: d.name,
          email: (prefillPatient && prefillPatient.email) || '',
          phone: d.phone || '',
          age: d.age != null ? String(d.age) : '',
          gender: d.gender || '',
          relationship: d.relationship || 'other',
          campus_member_id: d.id,
        }, false)}
      />
    );
  }

  if (phase === 'form') {
    return (
      <>
        {notice && (
          <div className="mt-4 text-[12px] text-amber-100 bg-amber-500/10 border border-amber-400/30 rounded-lg p-3">
            {notice} <a href="/register" className="underline font-semibold">Register first</a> or continue as a guest below.
          </div>
        )}
        <BookingPatientForm
          form={form} setForm={setForm} phoneTouched={phoneTouched} setPhoneTouched={setPhoneTouched}
          error={error} submitting={submitting} primary={primary} locked={locked}
          showRelationship={!selfOnly && (category === 'non_dependent' || category === 'dependent')}
          relationshipMode={category === 'non_dependent' ? 'affiliation' : 'family'}
          submitLabel={skipEmailOtp ? 'Confirm appointment' : 'Continue to email verification'}
          emailPlaceholder={skipEmailOtp ? 'Email' : 'Email (for OTP)'}
          contactNotice={skipEmailOtp
            ? 'Please ensure your email and phone number are correct — they will be used for appointment updates and hospital follow-up.'
            : undefined}
          onBack={() => {
            if (selfOnly) onBack();
            else if (category === 'self') setPhase('category');
            else if (category === 'dependent') setPhase(portalFaculty ? 'deps' : 'verify');
            else if (category === 'faculty_staff') setPhase('verify');
            else setPhase('category');
          }}
          onSubmit={() => {
            const msg = validateBookingForm(form, category, { skipEmailOtp });
            if (msg) {
              if (msg.includes('mobile')) setPhoneTouched(true);
              setError(msg);
              return;
            }
            if (skipEmailOtp) runSubmit();
            else setPhase('otp');
          }}
        />
      </>
    );
  }

  if (phase === 'otp' && !skipEmailOtp) {
    return (
      <>
        {error && <p className="mt-3 text-[11px] text-red-400">{error}</p>}
        <BookingOtpPanel
          slug={slug} primary={primary} name={form.name} email={form.email} phone={form.phone}
          onBack={() => setPhase('form')}
          onVerified={(otpPayload) => runSubmit(otpPayload)}
        />
        {submitting && <p className="text-[11px] text-zinc-400 mt-2">Submitting appointment…</p>}
      </>
    );
  }

  return null;
}
